// A recorded run turned into a portable, replayable format. `World.log`
// (see `createWorld`'s `record` option and `types.ts`'s `Command`) already
// holds everything a run's script needs; this file only snapshots it and
// plays it back through the same public commands (`assignPath`,
// `toggleHold`, `flyOn`) the original run used, so replay is exact by
// construction — it never touches simulation state directly. Pure and
// deterministic like the rest of `src/sim`: no Date, no Math.random, no DOM.
import type { Command, Vec } from './types.ts';
import type { World } from './world.ts';
import { createWorld, step, toggleHold, flyOn } from './world.ts';
import { assignPath } from './route.ts';
import { levelById } from './levels/index.ts';

export interface Replay {
  version: 1;
  levelId: string;
  W: number;
  H: number;
  seed: number;
  commands: Command[];
}

function cloneCommand(c: Command): Command {
  return c.t === 'path' ? { ...c, pts: c.pts.map((v: Vec) => ({ x: v.x, y: v.y })) } : { ...c };
}

/**
 * Snapshot of `world.log` so far, as an independent copy — commands appended
 * to `world.log` after this call do not retroactively change the Replay
 * already taken. Throws nothing; an unrecorded world (`log` undefined)
 * simply yields an empty command list.
 */
export function replayOf(world: World, levelId: string, W: number, H: number, seed: number): Replay {
  return { version: 1, levelId, W, H, seed, commands: (world.log ?? []).map(cloneCommand) };
}

function applyCommand(world: World, cmd: Command): void {
  if (cmd.t === 'flyOn') { flyOn(world); return; }
  const plane = world.planes.find(p => p.id === cmd.plane);
  if (!plane) {
    throw new Error(
      `replayWorld: plane ${cmd.plane} not found at tick ${cmd.tick} for a '${cmd.t}' command — ` +
      `the log doesn't match this level/seed, or a plane id was reused in a way replay can't resolve`
    );
  }
  if (cmd.t === 'path') assignPath(world, plane, cmd.pts, cmd.smooth !== undefined ? { smooth: cmd.smooth } : {});
  else toggleHold(world, plane);
}

/**
 * Replay a recorded run from scratch: `createWorld` with the same level
 * (looked up by `levelId`), `W`, `H` and `seed`, then advance tick by tick —
 * at each tick, apply every command recorded for it, in log order, then
 * `step()`. Commands name planes by id and are resolved against the replayed
 * world's own `planes`; if a command's plane doesn't exist at that point,
 * that is a bug in the log (or a mismatched level/seed) and this throws
 * rather than silently skipping it.
 *
 * With `untilTick` given, stops as soon as `world.tick` reaches it (having
 * already applied that tick's commands) — this is how a caller reproduces an
 * exact moment from a longer run, e.g. to compare against a live run that
 * was itself cut off at a fixed step count. Without it, stops one tick past
 * the last recorded command, or as soon as the world stops `'playing'` with
 * no commands left to apply, whichever comes first — enough to see the
 * outcome the recorded commands led to, without simulating forever into an
 * otherwise-uneventful `'playing'` world the log has nothing left to say
 * about.
 */
export function replayWorld(r: Replay, untilTick?: number): World {
  const world = createWorld(levelById(r.levelId), r.W, r.H, r.seed);
  const lastTick = r.commands.length ? r.commands[r.commands.length - 1].tick : -1;
  let idx = 0;
  for (;;) {
    while (idx < r.commands.length && r.commands[idx].tick === world.tick) {
      applyCommand(world, r.commands[idx]);
      idx++;
    }
    if (untilTick !== undefined) {
      if (world.tick >= untilTick) break;
    } else if (idx >= r.commands.length && (world.tick > lastTick || world.state !== 'playing')) {
      break;
    }
    step(world);
  }
  return world;
}
